<template>
	<div
		ref="$rootElement"
		class="user-assign-select-inline"
		:class="classAddons"
	>
		<SuiSelectListInline
			class="user-assign-select-inline__content"
			:automatic-min-height="false"
			@navigate="scrollToIndex"
		>
			<template #header>
				<UserAssignSelectInlineHeader
					:title="title"
					:search-query="searchQuery"
					:has-individual-time-allocation="
						hasIndividualTimeAllocation
					"
					:users-list="usersList"
					:selected-users="selectedUsers"
					:selected-roles="selectedRoles"
					:pinned-user-positions="pinnedUserPositions"
					:has-custom-allocation="hasCustomAllocation"
					:confirmed-allocated-times="confirmedAllocatedTimes"
					:allocated-times="allocatedTimes"
					:task-users-availability="taskUsersAvailability"
					:active-role-filter-id="activeRoleFilterId"
					:is-close-button-enabled="isCloseButtonEnabled"
					:has-auto-focus="hasAutoFocus"
					@close="onHeaderClose"
					@search="searchQuery = $event"
					@unselect="onUserClicked($event)"
					@update:has-custom-allocation="
						onUpdateHasCustomAllocation($event)
					"
					@update:allocated-times="
						$emit('update:allocated-times', $event)
					"
					@update:active-role-filter-id="onRoleFilterClicked"
				/>
			</template>
			<UserAssignSelectInlineList
				:filtered-user-list="filteredUserList"
				:is-modify-enabled="isModifyEnabled"
				:is-single-user-assign="isSingleUserAssign"
				:is-all-users-selected="isAllUsersSelected"
				:selected-users="selectedUsers"
				:selected-roles="selectedRoles"
				:user-group-list="userGroupList"
				:selected-user-group="selectedUserGroup"
				:loading-users-and-groups="isInlineLoading"
				:closed-groups="closedGroups"
				:header-element="headerElement"
				:task-users-availability="taskUsersAvailability"
				:active-role-filter-id="activeRoleFilterId"
				:grouped-virtual-list="groupedVirtualList"
				:virtual-list-attrs="virtualListAttrs"
				:measure-element="measureElement"
				@select-all="onSelectAll"
				@select-user-group="onSelectUserGroup"
				@deselect-user-group="onDeselectUserGroup"
				@toggle-group-dropdown="onToggleGroup"
				@toggle-all-users-selected="toggleAllUsersSelected"
				@user-clicked="onUserClicked"
				@role-clicked="onRoleClicked"
			/>
			<template #footer>
				<UserAssignSelectInlineFooter
					:task-id="taskId"
					:should-notify="shouldNotify"
					:active-role-filter-id="activeRoleFilterId"
					:is-apply-and-notify-visible="isApplyAndNotifyVisible"
					@update:should-notify="shouldNotify = $event"
					@assign-users="$emit('assign-users', shouldNotify)"
					@remove-role="onRemoveRole"
				/>
			</template>
		</SuiSelectListInline>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, toRefs, onMounted, watch, toRef } from 'vue';
import {
	SuiSelectListInline,
	type SuiSelectListGroupType,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import {
	type User,
	UserType,
	UserStatus,
	type Project,
	useUserAssignSelectInlineUsersAndGroupsLazyQuery,
	UserAssignSelectInlineUsersAndGroupsDocument,
	type UserAssignSelectInlineUsersAndGroupsQuery,
	type UserAssignSelectInlineUsersQuery,
	type Task,
	type UserAssignSelectUserFragment,
	type Role,
	useUserAssignSelectInlineAvailabilityQuery,
	type UserAssignSelectInlineAvailabilityQuery,
	type Contact,
	useUserAssignSelectInlineProjectMembersLazyQuery,
	UserAssignSelectInlineProjectMembersDocument,
} from '@/generated/graphql';
import uniq from 'lodash/uniq';
import {
	UserAssignGroups,
	type PinnedUserPositions,
	type UserAssignGroup,
	type UserAssignSelectRole,
	type UserAssignSelectAssigneesPayload,
} from '../../types';
import { FILTERED_USER_TYPES, UserAssignSelectSize } from '../../constants';
import { getInitials, sortUsers } from '../../utils';
import { type ApolloQueryResult } from '@apollo/client/core';
import { type ValueOf } from '@/types';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import UserAssignSelectInlineHeader from './UserAssignSelectInlineHeader.vue';
import UserAssignSelectInlineList from './UserAssignSelectInlineList/UserAssignSelectInlineList.vue';
import UserAssignSelectInlineFooter from './UserAssignSelectInlineFooter.vue';
import { secondsToTimeAllocation } from '@/utils/time';
import {
	useUserAssignRoleFilter,
	useUserAssignVirtualList,
} from '../../composables';

export type UserAssignSelectInlineProps = {
	title?: string;
	subGroupTitles?: string[];
	userIds: Array<User['id']>;
	assignedRoles?: Array<UserAssignSelectRole>;
	initialRoleIds?: Array<Role['id']>;
	isModifyEnabled: boolean;
	projectId?: Project['id'] | null;
	loadProjectMembers?: boolean;
	isSingleUserAssign?: boolean;
	isGroupedByRoles?: boolean;
	taskId?: Task['id'];
	size?: ValueOf<typeof UserAssignSelectSize>;
	hasIndividualTimeAllocation?: boolean;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	hasCustomAllocation?: Task['hasCustomAllocation'];
	filterByRoleId?: Role['id'];
	plannedDuration?: Task['durationPlanned'] | null;
	/**
	 * Contact ID to filter CUPO users by.
	 * Only shows CUPO users related to the specified contact
	 */
	contactId?: Contact['id'];
	/**
	 * Enable role selection in the dropdown list.
	 * Defaults to false to ensure roles are only shown where explicitly needed (e.g., task pages).
	 */
	enableRoleSelection?: boolean;
	startDate?: Task['startDate'] | null;
	dueDate?: Task['dueDate'] | null;
	isCloseButtonEnabled?: boolean;
	isApplyAndNotifyVisible?: boolean;
	hasAutoFocus?: boolean;
};

const props = withDefaults(defineProps<UserAssignSelectInlineProps>(), {
	title: undefined,
	subGroupTitles: () => [],
	projectId: undefined,
	loadProjectMembers: false,
	isSingleUserAssign: false,
	taskId: undefined,
	size: UserAssignSelectSize.MEDIUM,
	allocatedTimes: undefined,
	confirmedAllocatedTimes: undefined,
	hasCustomAllocation: false,
	filterByRoleId: undefined,
	plannedDuration: undefined,
	contactId: undefined,
	assignedRoles: () => [],
	initialRoleIds: () => [],
	enableRoleSelection: false,
	startDate: undefined,
	dueDate: undefined,
	isCloseButtonEnabled: false,
	isApplyAndNotifyVisible: false,
	hasAutoFocus: false,
});

const emit = defineEmits<{
	(e: 'update:assignees', payload: UserAssignSelectAssigneesPayload): void;
	(e: 'update:allocated-times', payload: TaskAssigneeInputDuration): void;
	(e: 'update:has-custom-allocation', value: boolean): void;
	(e: 'close'): void;
	(e: 'assign-users', shouldNotify: boolean): void;
	(e: 'role-replaced-with-user'): void;
}>();

const { isModifyEnabled, projectId, contactId } = toRefs(props);

const closedGroups = ref<UserAssignGroup['id'][]>([]);
const closedGroupsBeforeFilter = ref<UserAssignGroup['id'][]>([]);
const activeRoleFilterId = ref<UserAssignSelectRole['id'] | null>(null);
const $rootElement = ref<HTMLElement | null>(null);

const headerElement = () =>
	$rootElement.value?.querySelector('.sui-select-list-inline__header') ??
	null;
const listContentElement = () =>
	($rootElement.value?.querySelector(
		'.sui-select-list-inline__content'
	) as HTMLElement) ?? null;

const baseUsersQueryFilters = computed(() => ({
	userTypes: FILTERED_USER_TYPES,
	projectId: projectId.value,
	contactId: contactId.value,
}));

const baseProjectQueryParams = {
	projectParams: {
		projectId: projectId.value ? Number(projectId.value) : undefined,
	},
	loadProject: Boolean(props.loadProjectMembers && projectId.value),
};

const {
	load,
	result,
	loading: loadingUsersAndGroups,
	onResult: onResultAllUsers,
} = useUserAssignSelectInlineUsersAndGroupsLazyQuery({
	usersListInput: {
		filters: {
			...baseUsersQueryFilters.value,
		},
	},
	rolesListInput: {
		filters: {
			isActive: true,
		},
	},
	loadRoles: props.enableRoleSelection,
	...baseProjectQueryParams,
});

const isInlineLoading = computed(() => loadingUsersAndGroups.value);

const { load: loadProjectMembersQuery, onResult: onProjectMembersResult } =
	useUserAssignSelectInlineProjectMembersLazyQuery();

const loadUsersAndGroups = () => {
	load(
		UserAssignSelectInlineUsersAndGroupsDocument,
		{
			usersListInput: {
				filters: {
					...baseUsersQueryFilters.value,
					taskId: props.taskId,
				},
			},
			rolesListInput: {
				filters: {
					isActive: true,
				},
			},
			loadRoles: props.enableRoleSelection,
			...baseProjectQueryParams,
		},
		{
			fetchPolicy: 'network-only',
		}
	);
};

const searchQuery = ref('');
const shouldNotify = ref(false);

const selectedUsers = ref(props.userIds);
const selectedRoles = ref<UserAssignSelectRole[]>(props.assignedRoles);
const usersList = ref<UserAssignSelectUserFragment[]>([]);

const availabilityEnabled = computed(
	() => !!props.startDate && !!props.dueDate && usersList.value.length > 0
);

const availabilityVariables = computed(() => ({
	input: {
		...(props.taskId && { taskId: props.taskId }),
		userIds: usersList.value.map(({ id }) => id),
		startDate: props.startDate,
		dueDate: props.dueDate,
	},
}));

const { result: taskUsersAvailabilityResult } =
	useUserAssignSelectInlineAvailabilityQuery(availabilityVariables, {
		enabled: availabilityEnabled,
		fetchPolicy: 'network-only',
	});

const taskUsersAvailability = computed<
	UserAssignSelectInlineAvailabilityQuery['TaskUsersAvailability']
>(() => taskUsersAvailabilityResult.value?.TaskUsersAvailability ?? []);
const projectMembers = ref<Array<User['id']>>([]);

// Guard flag: prevents the watch([selectedUsers, selectedRoles]) handler
// from treating prop-synced values as user-initiated additions.
let isSyncingFromProps = false;

// Sync selectedUsers / selectedRoles when the parent passes new values
// (e.g. after an async query populates the list).  Without this,
// `ref(props.userIds)` only captures the initial value and later prop
// changes are lost.
watch(toRef(props, 'userIds'), (incoming) => {
	const current = selectedUsers.value;
	if (
		incoming.length !== current.length ||
		incoming.some((id, i) => id !== current[i])
	) {
		selectedUsers.value = [...incoming];
	}
});

watch(
	toRef(props, 'assignedRoles'),
	(incoming) => {
		const current = selectedRoles.value;
		const incomingIds = incoming.map((r) => r.id).sort();
		const currentIds = current.map((r) => r.id).sort();
		if (incomingIds.join() !== currentIds.join()) {
			isSyncingFromProps = true;
			selectedRoles.value = [...incoming];
		}
	},
	{ deep: true }
);

const pinnedUserPositions = ref<PinnedUserPositions>(new Map());

const normalizedSearchQuery = computed(() =>
	searchQuery.value.toLowerCase().trim()
);

const isSearchMatch = (text: string | undefined | null): boolean => {
	if (!text) {
		return false;
	}

	return text.toLowerCase().trim().includes(normalizedSearchQuery.value);
};

const filteredUserList = computed(() => {
	if (!usersList.value) {
		return [];
	}

	return usersList.value.filter((user) => {
		const nameMatches = isSearchMatch(user.name);

		if (props.enableRoleSelection) {
			const roleNameMatches = isSearchMatch(user.role?.name);
			return nameMatches || roleNameMatches;
		}

		return nameMatches;
	});
});

const projectMembersList = computed(() => {
	if (!filteredUserList.value) {
		return [];
	}

	return filteredUserList.value.filter((user) =>
		// In this group potential we could have INACTIVE users as well
		projectMembers.value.includes(user.id)
	);
});

const filterUnassignedUsers = (isCupoUser: boolean) => {
	if (!filteredUserList.value) {
		return [];
	}

	return filteredUserList.value.filter(
		(user) =>
			!projectMembers.value.includes(user.id) &&
			user.status !== UserStatus.Inactive &&
			(isCupoUser
				? user.type === UserType.Cupo
				: user.type !== UserType.Cupo)
	);
};

const unAssignedUserList = computed(() => filterUnassignedUsers(false));
const unAssignedCupoUserList = computed(() => filterUnassignedUsers(true));

const isAllProjectMembersSelected = computed(() => {
	if (projectMembersList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = projectMembersList.value.filter(
		(member) => !selectedUsers.value.includes(member.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const isAllNotAssignedUsersSelected = computed(() => {
	if (unAssignedUserList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = unAssignedUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const isAllNotAssigneCupoUsersSelected = computed(() => {
	if (unAssignedCupoUserList.value?.length === 0) {
		return false;
	}
	const notSelectedCupoUsers = unAssignedCupoUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedCupoUsers.length > 0 ? false : true;
});

const isAllUsersSelected = computed(() => {
	if (filteredUserList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = filteredUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const buildRoleGroupsForAssignRolesToTasks = (
	groups: UserAssignGroup[]
): UserAssignGroup[] => {
	const rolesList = result.value?.RolesList ?? [];

	const roleGroups = [...rolesList]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((role) => {
			let usersInRole = unAssignedUserList.value.filter(
				(user) => user.role?.id === role.id
			);

			if (activeRoleFilterId.value) {
				usersInRole = usersInRole.filter(
					(user) => !selectedUsers.value.includes(user.id)
				);
			}

			const isRoleMatchesSearch = isSearchMatch(role.name);

			const shouldShowRoleItem =
				!activeRoleFilterId.value &&
				(isRoleMatchesSearch || usersInRole.length > 0);

			const roleWithInitials = {
				...role,
				initials: role.initials || getInitials(role.name),
			};

			return {
				id: role.name,
				name: role.name,
				role: roleWithInitials,
				isRoleSelectable:
					props.enableRoleSelection &&
					shouldShowRoleItem &&
					isRoleMatchesSearch,
				userList: usersInRole,
				isAllUsersSelected: usersInRole.every((user) =>
					selectedUsers.value.includes(user.id)
				),
			};
		});

	if (activeRoleFilterId.value || normalizedSearchQuery.value !== '') {
		groups[0] = {
			...groups[0],
			userList: groups[0].userList.filter((user) => {
				const isVisibleByName =
					normalizedSearchQuery.value === '' ||
					isSearchMatch(user.name);
				const isAvailableForSelection =
					!activeRoleFilterId.value ||
					!selectedUsers.value.includes(user.id);

				return isVisibleByName && isAvailableForSelection;
			}),
		};
	}

	return [...groups, ...roleGroups];
};

const userGroupListToDisplay = computed(() => {
	let groups: UserAssignGroup[] = [
		{
			id: UserAssignGroups.ProjectMembers,
			name: props.subGroupTitles[0],
			userList: projectMembersList.value,
			isAllUsersSelected: isAllProjectMembersSelected.value,
			role: null,
			isRoleSelectable: false,
		},
	];

	if (props.enableRoleSelection) {
		groups = buildRoleGroupsForAssignRolesToTasks(groups);
	} else {
		if (props.isGroupedByRoles) {
			const groupedByRoles: Record<
				Role['name'],
				UserAssignSelectUserFragment[]
			> = unAssignedUserList.value.reduce(
				(acc, user) => {
					const roleId = user.role?.name || props.subGroupTitles[1];
					if (!acc[roleId]) {
						acc[roleId] = [];
					}
					acc[roleId].push(user);
					return acc;
				},
				{} as Record<Role['name'], UserAssignSelectUserFragment[]>
			);

			groups = [
				...groups,
				...Object.entries(groupedByRoles).map(([roleName, users]) => ({
					id: roleName,
					name: roleName,
					role: null,
					isRoleSelectable: false,
					userList: users,
					isAllUsersSelected: users.every((user) =>
						selectedUsers.value.includes(user.id)
					),
				})),
			];
		} else if (isModifyEnabled.value) {
			groups = [
				...groups,
				{
					id: UserAssignGroups.Other,
					name: props.subGroupTitles[1],
					userList: unAssignedUserList.value,
					isAllUsersSelected: isAllNotAssignedUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
				{
					id: UserAssignGroups.Cupo,
					name: props.subGroupTitles[2],
					userList: unAssignedCupoUserList.value,
					isAllUsersSelected: isAllNotAssigneCupoUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
			];
		}
	}

	return groups.filter(
		(group) => group.isRoleSelectable || group.userList.length > 0
	);
});

const { scrollToGroup } = useUserAssignRoleFilter({
	userGroupListToDisplay,
	activeRoleFilterId,
});

const scrollElement = computed(
	() => listContentElement() as HTMLElement | null
);

const { groupedVirtualList, virtualListAttrs, measureElement, scrollToIndex } =
	useUserAssignVirtualList({
		userGroupListToDisplay,
		closedGroups,
		scrollElement,
	});

const updateGroupsForRoleFilter = () => {
	if (!activeRoleFilterId.value) {
		// Role cleared - restore previous state
		closedGroups.value = [...closedGroupsBeforeFilter.value];
		closedGroupsBeforeFilter.value = [];
	} else {
		// Role filter active - save current state if not already saved, and collapse all groups except Project members and the active role
		if (closedGroupsBeforeFilter.value.length === 0) {
			closedGroupsBeforeFilter.value = [...closedGroups.value];
		}
		closedGroups.value = userGroupListToDisplay.value
			.filter(
				(group) =>
					group.role?.id !== activeRoleFilterId.value &&
					group.id !== UserAssignGroups.ProjectMembers
			)
			.map((group) => group.id);
	}
};

const onRoleFilterClicked = (roleId: UserAssignSelectRole['id']) => {
	if (activeRoleFilterId.value === roleId) {
		activeRoleFilterId.value = null;
	} else {
		activeRoleFilterId.value = roleId;
		scrollToGroup(listContentElement(), headerElement());
	}

	updateGroupsForRoleFilter();
};

const unSelectGroup = (group: string[], selected: string[]) => {
	selectedUsers.value = selected.filter(
		(userId) => !group.some((id) => id === userId)
	);
};

const updateAssignees = () => {
	const users =
		usersList.value.filter((user) =>
			selectedUsers.value.includes(user.id)
		) || [];
	const hasCupoUsers =
		usersList.value.filter((user) => user.type === UserType.Cupo).length >
		0;

	emit('update:assignees', {
		users,
		hasCupoUsers,
		shouldNotify: false,
		roles: selectedRoles.value,
	});
};

const onSelectAll = (isSelectAll: boolean) => {
	if (props.isSingleUserAssign) {
		selectedUsers.value = [];
		return;
	}

	if (isSelectAll) {
		selectedUsers.value = filteredUserList.value.map((user) => user.id);
	} else {
		selectedUsers.value = [];
	}

	updateAssignees();
};

const toggleAllUsersSelected = (
	group: UserAssignGroup,
	isSelectAll: boolean
) => {
	const selected = [...selectedUsers.value];

	if (!isSelectAll) {
		unSelectGroup(group.userList?.map((user) => user.id) ?? [], selected);
	} else {
		group.userList.forEach((user) => selected.push(user.id));
		selectedUsers.value = uniq(selected);
	}

	updateAssignees();
};

const copyAllocatedTimes = (): TaskAssigneeInputDuration => {
	const times: TaskAssigneeInputDuration = {
		users: {},
		roles: {},
	};

	if (!props.allocatedTimes) {
		return times;
	}

	props.allocatedTimes.users.forEach((time, userId) => {
		times.users[userId] = secondsToTimeAllocation(time);
	});

	props.allocatedTimes.roles.forEach((time, roleId) => {
		times.roles[roleId] = secondsToTimeAllocation(time);
	});

	return times;
};

const replaceActiveRoleWithUser = (
	userId: UserAssignSelectUserFragment['id']
) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	const roleTime = props.allocatedTimes?.roles.get(activeRoleFilterId.value);

	if (roleTime) {
		const updatedTimes = copyAllocatedTimes();

		updatedTimes.users[userId] = secondsToTimeAllocation(roleTime);

		emit('update:allocated-times', updatedTimes);
	}

	const replacedRole = selectedRoles.value.find(
		(role) => role.id === activeRoleFilterId.value
	);
	if (replacedRole) {
		pinnedUserPositions.value.set(userId, replacedRole);
	}

	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	emit('role-replaced-with-user');
};

const onUserClicked = (id: UserAssignSelectUserFragment['id']) => {
	if (props.isSingleUserAssign) {
		selectedUsers.value = [id];

		updateAssignees();
		return;
	}

	if (selectedUsers.value.includes(id)) {
		selectedUsers.value = selectedUsers.value.filter(
			(userId) => userId !== id
		);
		pinnedUserPositions.value.delete(id);
	} else {
		selectedUsers.value = [...selectedUsers.value, id];
		replaceActiveRoleWithUser(id);
	}

	updateAssignees();
};

const onRoleClicked = (role: UserAssignSelectRole) => {
	if (selectedRoles.value.some((r) => r?.id === role?.id)) {
		selectedRoles.value = selectedRoles.value.filter(
			(r) => r?.id !== role?.id
		);
	} else {
		selectedRoles.value = [...selectedRoles.value, role];
	}

	updateAssignees();
};

const onHeaderClose = () => {
	if (props.isCloseButtonEnabled && !activeRoleFilterId.value) {
		emit('close');
	} else if (activeRoleFilterId.value) {
		activeRoleFilterId.value = null;
		updateGroupsForRoleFilter();
	}
};

const onToggleGroup = (target: HTMLElement, group: UserAssignGroup) => {
	if (target.nodeName !== 'LABEL' && target.nodeName !== 'INPUT') {
		if (closedGroups.value.includes(group.id)) {
			closedGroups.value = closedGroups.value.filter(
				(id) => id !== group.id
			);
		} else {
			closedGroups.value.push(group.id);
		}
	}
};

const onRemoveRole = () => {
	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	updateGroupsForRoleFilter();
	updateAssignees();
};

const onUpdateHasCustomAllocation = (hasCustomAllocation: boolean) => {
	if (
		hasCustomAllocation &&
		props.plannedDuration &&
		(selectedUsers.value.length > 0 || selectedRoles.value.length > 0)
	) {
		const totalCount =
			selectedUsers.value.length + selectedRoles.value.length;
		const equalTime = Math.floor(props.plannedDuration / totalCount);

		const timeInputs: TaskAssigneeInputDuration = {
			users: {},
			roles: {},
		};

		selectedUsers.value.forEach((userId) => {
			timeInputs.users[userId] = secondsToTimeAllocation(equalTime);
		});

		selectedRoles.value.forEach((role) => {
			timeInputs.roles[role.id] = secondsToTimeAllocation(equalTime);
		});

		emit('update:allocated-times', timeInputs);
	}

	emit('update:has-custom-allocation', hasCustomAllocation);
};

// these are the user groups that are defined in the settings
const userGroupList = computed<SuiSelectListGroupType[]>(() => {
	if (!result.value?.UserGroupsList) {
		return [];
	}

	return result.value.UserGroupsList.map(({ id, name, users }) => ({
		id,
		name,
		items: users
			.filter(({ status }) => status !== UserStatus.Inactive)
			.map(({ id }) => id),
	}))
		.filter((group) => group.items.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name));
});

const selectedUserGroup = computed(
	(): SuiSelectListItemsAndGroupsValueType => ({
		itemIds: selectedUsers.value,
		groupIds: [],
	})
);

const onSelectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = uniq(
			selectedUsers.value.concat(selectedGroup.items || [])
		);

		updateAssignees();
	}
};

const onDeselectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = selectedUsers.value.filter(
			(id) => !selectedGroup.items?.includes(id)
		);
	}
};

const updateUsersList = (
	result: ApolloQueryResult<UserAssignSelectInlineUsersQuery>
) => {
	if (result.data?.UsersList) {
		usersList.value = sortUsers(result.data.UsersList);
	}

	if (result.data?.Project?.members) {
		const regularMembers = result.data.Project.members.map(
			(member) => member.id
		);
		const cupoUsers = result.data.Project.cupoUsers.map(
			(cupoUser) => cupoUser.id
		);

		projectMembers.value = [...regularMembers, ...cupoUsers];
	}
};

const updateInitialRoles = (
	result: ApolloQueryResult<UserAssignSelectInlineUsersAndGroupsQuery>
) => {
	if (props.initialRoleIds.length > 0 && result.data?.RolesList) {
		const fetchedRoles = result.data.RolesList;
		selectedRoles.value = fetchedRoles.filter((role) =>
			props.initialRoleIds.includes(role.id)
		);

		if (selectedRoles.value.length > 0) {
			updateAssignees();
		}
	}
};

const classAddons = computed(() => ({
	[`user-assign-select-inline--${props.size}`]: props.size,
	[`user-assign-select-inline--has-time-allocation`]:
		props.hasCustomAllocation,
}));

onResultAllUsers((result) => {
	updateUsersList(result);
	updateInitialRoles(result);

	if (activeRoleFilterId.value) {
		updateGroupsForRoleFilter();
	}
});

onProjectMembersResult((result) => {
	if (result.data?.Project) {
		const regularMembers = result.data.Project.members.map(
			(member) => member.id
		);
		projectMembers.value = [...regularMembers];
	}
});

onMounted(() => {
	loadUsersAndGroups();
});

watch(
	[selectedUsers, selectedRoles],
	(
		[newSelectedUsers, newSelectedRoles],
		[oldSelectedUsers, oldSelectedRoles]
	) => {
		// Skip allocated-time side-effects when syncing prop values
		if (isSyncingFromProps) {
			isSyncingFromProps = false;
			return;
		}

		if (props.allocatedTimes && oldSelectedUsers && oldSelectedRoles) {
			const addedUsers = newSelectedUsers.filter(
				(userId) => !oldSelectedUsers.includes(userId)
			);
			const addedRoles = newSelectedRoles.filter(
				(role) =>
					!oldSelectedRoles.some((oldRole) => oldRole.id === role.id)
			);

			if (addedUsers.length > 0 || addedRoles.length > 0) {
				const currentTimes = copyAllocatedTimes();

				addedUsers.forEach((userId) => {
					// Time can be present if user is replacing a role
					if (!currentTimes.users[userId]) {
						currentTimes.users[userId] = '0';
					}
				});

				addedRoles.forEach((role) => {
					currentTimes.roles[role.id] = '0';
				});

				emit('update:allocated-times', currentTimes);
			}
		}
	},
	{ deep: true }
);

watch(searchQuery, (newQuery, oldQuery) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	// Query change, expand groups with results
	if (newQuery !== '') {
		const groupsWithMatches: UserAssignGroup['id'][] = [];

		userGroupListToDisplay.value.forEach((group) => {
			if (group.userList.length > 0) {
				groupsWithMatches.push(group.id);
			}
		});

		closedGroups.value = userGroupListToDisplay.value
			.filter((group) => !groupsWithMatches.includes(group.id))
			.map((group) => group.id);
	}

	// Query cleared, filter still active - update groups and scroll to group
	if (oldQuery !== '' && newQuery === '') {
		updateGroupsForRoleFilter();
		scrollToGroup(listContentElement(), headerElement());
	}
});

watch(projectId, (newProjectId) => {
	if (newProjectId) {
		loadProjectMembersQuery(
			UserAssignSelectInlineProjectMembersDocument,
			{
				projectParams: {
					projectId: Number(newProjectId),
				},
			},
			{
				fetchPolicy: 'network-only',
				context: {
					queryDeduplication: false,
				},
			}
		);
	} else {
		projectMembers.value = [];
	}
});
</script>

<style lang="scss" scoped>
.user-assign-select-inline {
	height: 100%;

	&__content {
		// List content
		--sui-select-list-inline-gap: 0;
		--sui-select-list-content-max-height: none;
		--sui-select-list-width: 100%;
		--sui-select-list-button-padding-left: 2rem;

		// Search header
		--sui-select-list-header-with-search-header-padding: 0.5rem 0;
		--sui-select-list-header-with-search-header-border-bottom: 1px solid
			#{$neutral-20};
		--sui-select-list-header-with-search-title-font-size: 1.5rem;

		// Search Input
		--sui-select-list-search-padding: 1rem 0;

		font-size: 1.5rem;
		height: 100%;
		display: flex;
		flex-direction: column;
	}
}
</style>
